import _ from 'underscore';
import AtlStorage from './atl-storage';
import ProductConstants from './hamlet-product-constants';
import ProductContentDataModel from './hamlet-product-content-data-model';

export default class ProductContentData {
  storage: AtlStorage;
  stored: any;
  productConstants: ProductConstants;
  products: any;

  constructor() {
    this.storage = new AtlStorage();
    this.stored = this.storage.local.get('common.constants') || {};
    this.productConstants = new ProductConstants();

    this.products = { ...this.stored };
    _.each(this.productConstants.products, (data, key) => {
      this.products[key] = new ProductContentDataModel(data);
    });
  }

  has(productKey: string) {
    return !!this.products[productKey];
  }

  get(productKey: string) {
    return this.products[productKey];
  }

  create(product) {
    if (_.has(product, 'productKey')) {
      return this.add(product.productKey, this.createFromHamletApi(product));
    } else if (_.has(product, 'pluginKey')) {
      this.add(product.pluginKey + '.ondemand', this.createFromMarketplaceApi(product));
      return this.add(product.pluginKey, this.createFromMarketplaceApi(product));
    }
  }

  /*
   * Create a new entry based on a Product Object from the Hamlet API
   * If the product is a marketplace product, will use the productDescription (name)
   * and productKey to create a generic plugin entry. Will not contain a meaningful
   * description of the plugin :(
   * If it is not a marketplace plugin (highly unlikely), it will add an empty model
   */
  createFromHamletApi(product) {
    if (product.productType === 'ADDON' || product.productType === 'HOSTED_ADDON') {
      return {
        productTagLine: {
          'default': product.productDescription
        },
        productLongDescription: {
          'default': product.productDescription
        },
        marketplace: true,
        listOrder: 99,
        images: {
          xxx: '/sc-shared/img/mpac-icon.png',
          xx:  'https://marketplace.atlassian.com/plugins/' + product.productKey + '/icon'
        },
        moreLink: 'https://marketplace.atlassian.com/plugins/' + product.productKey,
        onDemand: {
          available: false,
          key: product.productKey + '.ondemand'
        }
      };
    }
    return {};
  }

  /*
   * Create a new entry based on a Plugin Object from the MPAC API. Will contain a
   * verbose description, potentially a populated OnDemand object with/without cross-sell
   * text and/or a eula.
   */
  createFromMarketplaceApi(plugin) {

    var content = {
        productTagLine: {
            'default': plugin.name
        },
        productLongDescription: {
            'default': plugin.summary
        },
        marketplace: true,
        listOrder: 99,
        images: {
            xxx: '/sc-shared/img/mpac-icon.png',
            xx:  'https://marketplace.atlassian.com/plugins/' + plugin.pluginKey + '/icon'
        },
        moreLink: 'https://marketplace.atlassian.com/plugins/' + plugin.pluginKey,
        onDemand: {
            available: false,
            key: null
        }
    };

    // Check for a EULA
    if (_.has(plugin, 'version') && _.has(plugin.version, 'links')) {
        var eula = _.where(plugin.version.links, { 'rel': 'eula' });
        if (eula.length > 0) {
            content.eula = eula[0].href;
        }
    }

    // Check for OnDemand deployment
    if (_.isObject(plugin.deployment) &&
      plugin.deployment.onDemand &&
      _.isObject(plugin.version) &&
      _.isObject(plugin.version.marketplaceType) &&
      _.isString(plugin.version.marketplaceType.type)) {

        if (plugin.version.marketplaceType.type === 'Paid via Atlassian') {
          content.onDemand = {
            available: true,
            key: plugin.pluginKey + '.ondemand'
          };
        } else if (plugin.version.marketplaceType.type === 'Free') {
          content.onDemand = {
            available: false,
            crossSell: 'This add-on is free and pre-installed on your Cloud instance.',
            key: null
          };
        } else if (plugin.version.marketplaceType.type === 'Paid via Vendor') {
          content.onDemand = {
            available: false,
            crossSell: 'This add-on is pre-installed on your Cloud instance. Contact the vendor for a license.',
            key: null
          };
        }
    }

    return content;
  }

  /*
   * Provided a productKey and a contentData object, add a new entry to the list
   * and update the localStorage
   */
  add(productKey, data) {
    this.products[productKey] = new ProductContentDataModel(data);
    this.storage.local.set('common.constants', this.products);
    return this.products[productKey];
  }

}
